import type { z } from "zod/v4";
import { Flex } from "@/components/Flex/Flex";
import { Text } from "@/components/Text";
import type { NextStepEventSchema } from "@/schemas/sections";
import { CalendarIcon } from "./icons";

/** The dated эфир, stated once directly under the headline. The live page named
 *  no date at all, so a registrant had to reopen the email to find out when to
 *  show up.
 *
 *  🛑 `data-surface="light"` is load-bearing, not decoration. Lime is a LIGHT
 *  surface, so ADR-0097 puts the action back to purple; without the attribute a
 *  future editor reading "bright card" as a dark band would flip `--action-bg`
 *  to lime and render a lime button on a lime card. The artboard drew this
 *  button as a near-black ink pill, which is a third action colour with no
 *  `.ds-*` atom — `docs/rules/styling.md` routes CTAs through `.ds-btn-action`
 *  instead, so the button is purple here and the artboard follows the code.
 *
 *  The calendar link carries NO `download` attribute on purpose: a plain link to
 *  a `text/calendar` resource is what raises the iOS "Add to Calendar" sheet,
 *  while `download` can divert it to Files. 75% of this traffic is mobile. */
export function EventCard({ cfg }: { cfg: z.input<typeof NextStepEventSchema> }) {
  return (
    <div
      data-component="EventCard"
      data-surface="light"
      className="mx-auto mt-4 max-w-xl rounded-3xl bg-cta p-4 text-left md:mt-6 md:p-6"
    >
      <Text variant="heading" as="p" className="text-fg">
        {cfg.dateLine}
      </Text>
      {cfg.note && (
        <Text variant="body-sm" as="p" className="mt-2 text-fg-soft">
          {cfg.note}
        </Text>
      )}
      {cfg.calendarHref && (
        <Flex
          as="a"
          href={cfg.calendarHref}
          align="center"
          gap={8}
          data-track="lm_next.cta.calendar"
          className="ds-btn ds-btn-action mt-4 inline-flex w-auto no-underline"
        >
          <CalendarIcon />
          {cfg.calendarLabel ?? "Добавить в календарь"}
        </Flex>
      )}
    </div>
  );
}

EventCard.displayName = "EventCard";
